import React from "react";
import { Check, Sparkles } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";

export const Pricing = () => {
  const { content } = useSiteContent();
  const tiers = content.pricing_tiers || [];
  if (content.pricing_enabled === false) return null;
  if (tiers.length === 0) return null;

  return (
    <section
      id="pricing"
      className="py-24 lg:py-32 bg-[#F4EFE6] relative overflow-hidden"
    >
      <div className="absolute inset-0 grain opacity-50" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#CBA358] font-semibold">
              {content.pricing_eyebrow}
            </div>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 text-[#0A2342] leading-[1.05] text-balance">
              {content.pricing_headline}
            </h2>
            {content.pricing_description && (
              <p className="mt-5 text-[#5C636A] leading-relaxed max-w-xl">
                {content.pricing_description}
              </p>
            )}
          </div>

          {content.pricing_sticker_visible && content.pricing_sticker_text && (
            <div
              data-testid="pricing-sticker"
              className="relative inline-block transform rotate-[-4deg] hover:rotate-0 transition-transform duration-300"
            >
              <div className="bg-[#0A2342] text-[#CBA358] px-7 py-5 shadow-[6px_6px_0px_#CBA358] relative">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#FDFBF7]/80">
                  <Sparkles className="w-3 h-3" /> Ongoing Offer
                </div>
                <div className="font-display font-bold text-3xl sm:text-4xl mt-1 leading-none">
                  {content.pricing_sticker_text}
                </div>
                {content.pricing_sticker_subtext && (
                  <div className="text-xs mt-2 text-[#FDFBF7]/70 max-w-[200px]">
                    {content.pricing_sticker_subtext}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={`grid gap-6 ${tiers.length === 2 ? "lg:grid-cols-2" : tiers.length === 1 ? "lg:max-w-md mx-auto" : "lg:grid-cols-3"}`}>
          {tiers.map((t, i) => (
            <div
              key={t.id || i}
              data-testid={`pricing-tier-${i}`}
              className={`relative bg-white p-8 transition-all hover:-translate-y-1 ${
                t.highlight
                  ? "border-2 border-[#0A2342] shadow-[8px_8px_0px_#CBA358]"
                  : "border border-[#E5DFD3]"
              }`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-7 bg-[#0A2342] text-[#CBA358] px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-bold">
                  Most Popular
                </div>
              )}

              <h3 className="font-display text-2xl text-[#0A2342]">{t.title}</h3>
              {t.description && (
                <p className="mt-2 text-sm text-[#5C636A] leading-relaxed">
                  {t.description}
                </p>
              )}

              <div className="mt-6 flex items-baseline gap-3">
                <span className="font-display text-5xl font-bold text-[#0A2342] leading-none">
                  {t.price_text}
                </span>
                {t.original_price && (
                  <span className="font-display text-2xl text-[#5C636A] line-through opacity-60">
                    {t.original_price}
                  </span>
                )}
              </div>

              {(t.features || []).length > 0 && (
                <ul className="mt-7 space-y-3">
                  {(t.features || []).map((f, fi) => (
                    <li key={fi} className="flex items-start gap-3 text-[15px] text-[#3C4147]">
                      <span className="mt-0.5 w-5 h-5 flex items-center justify-center bg-[#CBA358]/20 text-[#0A2342] shrink-0">
                        <Check className="w-3 h-3" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              )}

              <a
                href="#booking"
                data-testid={`pricing-tier-cta-${i}`}
                className={`mt-8 inline-flex w-full items-center justify-center gap-2 px-6 py-3 text-sm uppercase tracking-wider font-medium transition-colors ${
                  t.highlight
                    ? "bg-[#0A2342] text-white hover:bg-[#173B63]"
                    : "border-2 border-[#0A2342] text-[#0A2342] hover:bg-[#0A2342] hover:text-white"
                }`}
              >
                {t.cta_text || "Book Now"} →
              </a>
            </div>
          ))}
        </div>

        {content.pricing_footnote && (
          <p className="mt-10 text-center text-sm text-[#5C636A] italic max-w-2xl mx-auto">
            {content.pricing_footnote}
          </p>
        )}
      </div>
    </section>
  );
};

export default Pricing;
